import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@reflex/ui/components/ui/button';
import { cn } from '@reflex/ui/lib/utils';

/** Short, human-scannable form of a git commit SHA (first 7 chars). */
function shortCommit(commit: string | null): string | null {
  const trimmed = commit?.trim();
  return trimmed ? trimmed.slice(0, 7) : null;
}

export function VersionSection({
  serverVersion,
  clientVersion,
  serverCommit,
  clientCommit,
  updateAvailable,
}: {
  serverVersion: string | null;
  clientVersion: string;
  serverCommit?: string | null;
  clientCommit?: string | null;
  updateAvailable: boolean;
}) {
  const borderClass = updateAvailable
    ? 'border-status-warning/30 bg-status-warning/10'
    : 'border-border bg-muted/20';

  const clientHash = shortCommit(clientCommit ?? null);
  const serverHash = shortCommit(serverCommit ?? null);

  return (
    <div className={cn('rounded-md border p-3 space-y-2.5', borderClass)}>
      {updateAvailable && (
        <div className="flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-status-warning shrink-0" />
          <span className="text-xs font-medium text-status-warning">Version mismatch</span>
        </div>
      )}
      <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
        <span className="text-muted-foreground">Client</span>
        <span data-testid="client-version" className="font-mono">
          v{clientVersion}
          {clientHash && (
            <span data-testid="client-commit" className="text-muted-foreground">
              {' · '}
              {clientHash}
            </span>
          )}
        </span>
        <span className="text-muted-foreground">Server</span>
        <span
          data-testid="server-version"
          className={cn('font-mono', updateAvailable && 'text-status-warning')}
        >
          {serverVersion ? `v${serverVersion}` : '—'}
          {serverVersion && serverHash && (
            <span
              data-testid="server-commit"
              className={cn('text-muted-foreground', updateAvailable && 'text-status-warning/80')}
            >
              {' · '}
              {serverHash}
            </span>
          )}
        </span>
      </div>
      {updateAvailable && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6 gap-1.5 text-xs font-medium text-status-warning"
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="h-3 w-3" />
          Refresh now
        </Button>
      )}
    </div>
  );
}
